import Alert from "@mui/material/Alert";
import { Bot as BotIcon } from "lucide-react";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useIdSuggestions } from "@/shared/components/filters/useSuggestionSources";
import { useAuditIdentityOptions } from "@/shared/components/principal";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  filterToCondition,
  useQuickSearchHotkey,
  useResourceView,
  ViewPicker,
  SaveViewControl,
  useLoadedView,
  useRecordTableState,
  useResourceTableContext,
  useRestoredTableState,
  useViewFromLink,
  type FiltersHandle,
  type FilterDefinition,
  type QuickSearchProperty,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import {
  DEVICE_FILTER_FIELDS,
  hydrateFilterDefinitions,
} from "@/shared/components/ResourceView/Filters/catalog";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { TargetEntityType } from "@/shared/domain/customFields";
import { type Device } from "@/shared/domain/devices";
import { devicesTableColumns } from "@/shared/domain/devices/DevicesTableColumns";
import { deviceQueryKeys } from "@/shared/domain/devices/hooks/deviceQueryKeys";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";
import { useAllDeviceTypesByOrg } from "@/shared/domain/deviceTypes/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  andConditions,
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
  type MetadataKey,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useDeviceTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  cellStyles,
  colId,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  renderDashOrValue,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";
import type { Condition } from "@/types";

import { CreateDeviceButton } from "./CreateDevice";
import styles from "./DevicesList.module.css";

// Devices have no name, so quick search always targets the device ID.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "device_id", name: "Device ID", fallback: true },
];

function toPickerOption(column: ColDef<Device>): ColumnPickerOption {
  return { id: colId(column), label: column.headerName ?? colId(column) };
}

function buildMetadataColumn(metadataKey: string): ColDef<Device> {
  const id: MetadataKey = `metadata.${metadataKey}`;
  return {
    colId: id,
    headerName: metadataKey,
    // Keeps a metadata column readable when the grid has more columns than
    // fit, instead of letting its flex width shrink it to nothing.
    minWidth: 120,
    valueGetter: (p) => p.data?.metadata[metadataKey],
    valueFormatter: (p) => formatMetadataValue(p.value),
    cellRenderer: renderDashOrValue,
    // The search API cannot sort devices by a metadata value.
    sortable: false,
  };
}

const DEVICE_TYPE_FIELD = "device_type_id";

/**
 * The device's type, by name, linked to the type's page; the `dt_` ID stands
 * in for the name until the org's types load. Not sortable: the search API
 * sorts by the stored ID, which is not the order of names.
 */
function buildTypeColumn(
  typeNames: ReadonlyMap<string, string>,
): ColDef<Device> {
  return {
    colId: DEVICE_TYPE_FIELD,
    headerName: "Type",
    minWidth: 140,
    sortable: false,
    valueGetter: (p) => {
      const deviceTypeId = p.data?.device_type_id;
      return deviceTypeId
        ? (typeNames.get(deviceTypeId) ?? deviceTypeId)
        : undefined;
    },
    cellRenderer: (p: ICellRendererParams<Device, string | undefined>) => {
      const deviceTypeId = p.data?.device_type_id;
      if (!deviceTypeId) {
        return renderDashOrValue(p);
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createDeviceTypeRoute(deviceTypeId)}
          className={cellStyles.primaryContent}
        >
          {p.value}
        </RobotoLinkHoverUnderline>
      );
    },
  };
}

interface DevicesListProps {
  /**
   * Conditions that limit which devices the list shows, such as one device
   * type's devices. A device must match all of them as well as the user's
   * filters.
   */
  conditions?: Condition[];
  /** Actions the Selection menu offers for the selected devices. */
  selectionOptions?: SelectionOption<Device>[];
  /** Right-aligned toolbar content, e.g. the page's "New Device" button. */
  actions?: React.ReactNode;
  /** Device type the empty state's New Device form starts on. */
  deviceTypeId?: string;
}

export function DevicesList({
  conditions,
  selectionOptions,
  actions,
  deviceTypeId,
}: DevicesListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useDeviceTablePreferences();

  // Loads every device in the org to collect the metadata keys offered as
  // columns. A metadata-keys route for devices, like the one datasets have,
  // would avoid that load.
  const allDevicesQuery = useAllDevicesByOrg(resolvedOrgId);
  const metadataKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const device of allDevicesQuery.data ?? []) {
      Object.keys(device.metadata).forEach((key) => keys.add(key));
    }
    return [...keys].sort();
  }, [allDevicesQuery.data]);

  // The Type column and filter exist only when `release-asset-store` is on
  // for the org.
  const assetStoreEnabled = useCheckFeatureFlag(
    "release-asset-store",
    resolvedOrgId,
  );
  const deviceTypes = useAllDeviceTypesByOrg(resolvedOrgId).data;
  const staticColumns = React.useMemo<ColDef<Device>[]>(() => {
    if (!assetStoreEnabled) {
      return devicesTableColumns;
    }
    const typeColumn = buildTypeColumn(
      new Map(
        (deviceTypes ?? []).map((deviceType) => [
          deviceType.device_type_id,
          deviceType.name,
        ]),
      ),
    );
    // The Type column goes right after Device ID, the first static column.
    return [
      devicesTableColumns[0],
      typeColumn,
      ...devicesTableColumns.slice(1),
    ];
  }, [assetStoreEnabled, deviceTypes]);

  // The users, devices and invocations that the `created_by` / `modified_by`
  // filters offer.
  const identityOptions = useAuditIdentityOptions(resolvedOrgId);
  const idSuggestions = useIdSuggestions(resolvedOrgId);

  const [initialColumnIds] = React.useState(() => preferences.columns);

  const customFields = useCustomFieldGridIntegration<Device>({
    entityType: TargetEntityType.Device,
    preferenceOrder: initialColumnIds,
    savePreferences,
  });

  const columns = React.useMemo<ColDef<Device>[]>(
    () =>
      buildColumnsForPreferences<Device>({
        staticColumns,
        dynamicColumns: [
          ...customFields.customFieldColumns,
          ...metadataKeys.map(buildMetadataColumn),
        ],
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [
      customFields.customFieldColumns,
      customFields.effectivePreferenceOrder,
      metadataKeys,
      staticColumns,
    ],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...staticColumns.map(toPickerOption),
      ...customFields.columnPickerOptions,
      ...metadataKeys.map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [customFields.columnPickerOptions, metadataKeys, staticColumns],
  );

  const quickSearchState = useQuickSearchHotkey();

  // A `?view=` link is resolved in the route loader, so the View it names
  // is available here, before the table's first query is composed. Seeding
  // it keeps a shared link from rendering an unfiltered page first.
  const linked = useViewFromLink<Device>({
    getRowId: (row) => row.device_id,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });
  const restored = useRestoredTableState({
    target: QueryTarget.Devices,
    link: linked,
  });
  const view = useResourceView<Device>(restored.options);

  // Which saved View is applied, and the routes in and out of one.
  const viewSession = useLoadedView(view, restored.view);
  useRecordTableState({
    target: QueryTarget.Devices,
    view,
    loadedView: viewSession.loadedView,
  });
  useResourceTableContext({
    target: QueryTarget.Devices,
    view,
    session: viewSession,
  });
  // Lets the View picker's empty state open the filter menu on a field.
  const filtersRef = React.useRef<FiltersHandle>(null);

  const filterDefinitions: FilterDefinition[] = React.useMemo(() => {
    const typeFilter: FilterDefinition[] = assetStoreEnabled
      ? [
          {
            type: "enum",
            field: DEVICE_TYPE_FIELD,
            name: "Type",
            // A device may have no type.
            nullable: true,
            options: (deviceTypes ?? []).map((deviceType) => ({
              label: deviceType.name,
              value: deviceType.device_type_id,
            })),
          },
        ]
      : [];
    return [
      ...hydrateFilterDefinitions(DEVICE_FILTER_FIELDS, {
        identityOptions,
      }).map((definition) =>
        definition.type === "string" && definition.field === "device_id"
          ? { ...definition, suggestions: idSuggestions?.device }
          : definition,
      ),
      ...typeFilter,
      ...customFields.filterDefinitions,
    ];
  }, [
    assetStoreEnabled,
    customFields.filterDefinitions,
    deviceTypes,
    identityOptions,
    idSuggestions,
  ]);

  const filterCondition =
    view.filters.type === "structured" && view.filters.filters.length > 0
      ? filterToCondition(view.filters.filters, {
          matchMode: view.filters.matchMode,
        })
      : undefined;

  const query = composeQueryBody({
    scopeCondition: andConditions(conditions),
    filterCondition,
    sort: view.table.sort,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Devices,
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: deviceQueryKeys.list(resolvedOrgId, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<Device>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const loading = deriveLoadingState({
    isLoading: submitQuery.isLoading || pageQuery.isLoading,
    isFetching: submitQuery.isFetching || pageQuery.isFetching,
  });
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <QuickSearch
        open={quickSearchState.isOpen}
        onClose={quickSearchState.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder={"Search by device ID…"}
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ViewPicker
            target={QueryTarget.Devices}
            loadedView={viewSession.loadedView}
            onSelectView={viewSession.selectView}
            seededViewId={viewSession.seededViewId}
            arrivalClaimed={restored.arrivalClaimed}
            onClearView={viewSession.clearView}
            linkFor={viewSession.linkFor}
            filterDefinitions={filterDefinitions}
            onStartFilter={(field) => filtersRef.current?.openWithField(field)}
          />
          <QuickSearchButton
            onClick={quickSearchState.open}
            label="Find devices"
          />
          <ResourceView.Filters
            ref={filtersRef}
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<Device>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
          {actions && <div className={styles.actionsRight}>{actions}</div>}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          trailingActions={
            <SaveViewControl
              view={view}
              target={QueryTarget.Devices}
              session={viewSession}
            />
          }
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<Device>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent={
            <ResourceView.EmptyState
              hasActiveFilters={view.hasActiveFilters}
              noData={{
                icon: BotIcon,
                title: "No devices yet",
                description:
                  "Register the robots and systems that send data to Roboto.",
                action: (
                  <CreateDeviceButton
                    variant="secondary"
                    align="center"
                    deviceTypeId={deviceTypeId}
                  />
                ),
              }}
              noMatches={{
                icon: BotIcon,
                title: "No matching devices",
                description: "Try adjusting or clearing your filters.",
              }}
            />
          }
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
        />
      </ResourceView.Root>
    </>
  );
}
